import { useState } from 'react';
import { Modal } from '../common/Modal';
import { X } from 'lucide-react';
import type { Certificate } from '../../hooks/useCertificates';
import type { Holder } from '../../utils/holders';
import { alteracoesDoCertificado, type AlteracoesDoCertificado } from '../../utils/certificateFolders';
import { CertificateHolderSelect } from './CertificateHolderSelect';

interface CertificateEditModalProps {
  certificate: Certificate;
  holders: Holder[];
  onClose: () => void;
  /** Envia só o que mudou (PATCH /certificates/{id}). */
  onSave: (alteracoes: AlteracoesDoCertificado) => Promise<void>;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--color-bg-root)',
  border: '1px solid var(--color-border-default)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
};

/**
 * D23: editar o rótulo e o titular (a pasta) de um certificado já cadastrado.
 * O arquivo e a senha não mudam por aqui: para isso, remover do Lictor e cadastrar de novo.
 */
export function CertificateEditModal({ certificate, holders, onClose, onSave }: CertificateEditModalProps) {
  const [label, setLabel] = useState(certificate.label);
  const [holderId, setHolderId] = useState(certificate.holder_id || '');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isBusy) return;
    const alteracoes = alteracoesDoCertificado(certificate, { label, holderId });
    if (Object.keys(alteracoes).length === 0) {
      onClose();
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      await onSave(alteracoes);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar o certificado.');
      setIsBusy(false);
    }
  };

  const titularAtual = certificate.holder_id && certificate.holder_name
    ? { id: certificate.holder_id, nome: certificate.holder_name }
    : null;

  return (
    <Modal pedirParaFechar={onClose} protegerDigitacao>
      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '440px', padding: 'var(--space-8)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>Editar certificado</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="cert-edit-label" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Nome para identificar</label>
            <input
              id="cert-edit-label"
              value={label}
              maxLength={200}
              onChange={(e) => setLabel(e.target.value)}
              style={inputStyle}
            />
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
              Em branco, volta a ser o nome que está no certificado ({certificate.subject_cn}).
            </span>
          </div>

          <CertificateHolderSelect
            id="cert-edit-holder"
            value={holderId}
            onChange={setHolderId}
            holders={holders}
            titularAtual={titularAtual}
          />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
            <button type="button" onClick={onClose} style={{ flex: '1 1 120px', padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isBusy}
              style={{ flex: '1 1 120px', padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.6 : 1 }}
            >
              {isBusy ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
